import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import SwitchInput from "../Shared/Inputs/SwitchInput";
import SelectInput from "../Shared/Inputs/SelectInput";
import { TextInput } from "../Shared/Inputs/TextInput";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import InsertMediaModal from "../MediaManager/InsertMediaModal";
import BunnyVideoSelectModal from "./BunnyVideoSelectModal";
import { useWindowDisplays } from "../../data/windowDisplays/useWindowDisplays";
import { useWindowDisplayMutations } from "../../data/windowDisplays/useWindowDisplayMutations";
import { useWindowDisplayAds } from "../../data/windowDisplays/useWindowDisplayAds";
import useAgencyMedia from "../../data/useAgencyMedia.js";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { WindowDisplayCreate, WindowDisplayUpdate } from "../../models/windowDisplay/WindowDisplay";
import { WindowDisplayAdCreate, WindowDisplayAdUpdate } from "../../models/windowDisplay/WindowDisplayAd";
import { CDN_URL } from "../../services/config";
import { IMedia } from "../../models/media/Media";
import useUserContext from "../../contexts/UserContext";
import { QuestionMarkCircleIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import Modal from "../Shared/Modal";

import Horizontal1 from "../../assests/WindowDisplaysAdExamples/Horizontal1.png"
import Horizontal2 from "../../assests/WindowDisplaysAdExamples/Horizontal2.png"
import Horizontal3 from "../../assests/WindowDisplaysAdExamples/Horizontal3.png"
import Vertical1 from "../../assests/WindowDisplaysAdExamples/Vertical1.png"
import Vertical2 from "../../assests/WindowDisplaysAdExamples/Vertical2.png"
import Vertical3 from "../../assests/WindowDisplaysAdExamples/Vertical3.png"

export default function WindowDisplaySettings({ agencyId }: { agencyId: string }) {
  const { user: { agency, identity } } = useUserContext();
  const { windowDisplay } = useWindowDisplays(agencyId);
  const { create, update } = useWindowDisplayMutations(agencyId);
  const { saveHandlers: adSaveHandlers } = useDefaultCRUDHandlers("Window Display Ad");
  const { media: agencyMedia } = useAgencyMedia();
  const { saveHandlers } = useDefaultCRUDHandlers("Window Display");

  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showVideoPicker, setShowVideoPicker] = useState(false);
  const [openLayouts, setOpenLayouts] = useState(false);
  const [editingAd, setEditingAd] = useState<WindowDisplayAdCreate | WindowDisplayAdUpdate | null>(null);
  const [newAd, setNewAd] = useState<{ mediaId?: string; videoUrl?: string }>({});
  const [selectedMedia, setSelectedMedia] = useState<any>(null);
  const [showUrls, setShowUrls] = useState(false);

  const windowDisplayId = windowDisplay.data?.data?.id;
  const { ads, videos, create: createAd, update: updateAd, remove: removeAd } = useWindowDisplayAds(windowDisplayId || "");

  const { register, getValues, setValue, watch, handleSubmit, reset, control } =
    useForm({
      defaultValues: {
        backgroundColour: "#FFFFFF",
        textColour: "#000000",
        featureInstead: false,
        hideIcons: false,
        logoStyle: 0,
        logoWidth: undefined,
        layoutStyle: 0,
        qrCodeText: "",
        timePerScreen: undefined,
        totalProperties: undefined,
        propertiesBetweenAds: undefined,
      },
    });

  // Load existing window display data when available
  useEffect(() => {
    if (windowDisplay.data?.data) {
      const existingDisplay = windowDisplay.data.data;
      reset({
        backgroundColour: existingDisplay.backgroundColour || "#FFFFFF",
        textColour: existingDisplay.textColour || "#000000",
        featureInstead: existingDisplay.featureInstead || false,
        hideIcons: existingDisplay.hideIcons || false,
        logoStyle: logoStyleOptions.findIndex(opt => opt.value === existingDisplay.logoStyle) || 0,
        logoWidth: existingDisplay.logoWidth as any,
        layoutStyle: layoutStyleOptions.findIndex(opt => opt.value === existingDisplay.layoutStyle) || 0,
        qrCodeText: existingDisplay.qrCodeText || "",
        timePerScreen: existingDisplay.timePerScreen as any,
        totalProperties: existingDisplay.totalProperties as any,
        propertiesBetweenAds: existingDisplay.propertiesBetweenAds as any,
      });
    }
  }, [windowDisplay.data?.data, reset]);

  const logoStyleOptions = [
    { id: 0, name: "Hidden", value: "hidden" },
    { id: 1, name: "Light", value: "light" },
    { id: 2, name: "Dark", value: "dark" }
  ];

  const layoutStyleOptions = [
    { id: 0, name: "Bar Bottom / 3 Images", value: "barBottom3Images", example: null },
    { id: 1, name: "Bar Right / 1 Image", value: "barRight1Image", example: null },
    { id: 2, name: "Bar Right / 4 Images", value: "barRight4Images", example: null },
    { id: 3, name: "New Style 1 Horizontal", value: "newStyle1Horizontal", example: Horizontal1 },
    { id: 4, name: "New Style 2 Horizontal", value: "newStyle2Horizontal", example: Horizontal2 },
    { id: 5, name: "New Style 3 Horizontal", value: "newStyle3Horizontal", example: Horizontal3 },
    { id: 6, name: "New Style 1 Vertical", value: "newStyle1Vertical", example: Vertical1 },
    { id: 7, name: "New Style 2 Vertical", value: "newStyle2Vertical", example: Vertical2 },
    { id: 8, name: "New Style 3 Vertical", value: "newStyle3Vertical", example: Vertical3 },
  ];

  const onSubmit = (data: any) => {
    // Convert select IDs to values
    data.logoStyle = logoStyleOptions.find(x => x.id == data.logoStyle)?.value;
    data.layoutStyle = layoutStyleOptions.find(x => x.id == data.layoutStyle)?.value;

    // Prepare the data for API
    const windowDisplayData = {
      backgroundColour: data.backgroundColour,
      textColour: data.textColour,
      featureInstead: data.featureInstead,
      hideIcons: data.hideIcons,
      logoStyle: data.logoStyle,
      logoWidth: data.logoWidth ? parseInt(data.logoWidth) : undefined,
      layoutStyle: data.layoutStyle,
      qrCodeText: data.qrCodeText,
      timePerScreen: data.timePerScreen ? parseInt(data.timePerScreen) : undefined,
      totalProperties: data.totalProperties ? parseInt(data.totalProperties) : undefined,
      propertiesBetweenAds: data.propertiesBetweenAds ? parseInt(data.propertiesBetweenAds) : undefined,
      agencyId: agencyId,
    };

    if (windowDisplay.data?.data) {
      // Update existing window display
      const existingDisplay = windowDisplay.data.data;
      const updateData: WindowDisplayUpdate = {
        ...windowDisplayData,
        id: existingDisplay.id,
      };
      update.mutate(updateData, {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
        }
      });
    } else {
      // Create new window display
      const createData: WindowDisplayCreate = windowDisplayData;
      create.mutate(createData, {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
        }
      });
    }
  }

  const backgroundColour = watch("backgroundColour")
  const textColour = watch("textColour")

  // Handle media selection from InsertMediaModal
  const handleMediaInsert = (sourceType: string, mediaType: string, value: any, width?: any, align?: any) => {
    if (sourceType === "Media Manager" && value && typeof value === "object" && value.id) {
      // Media Manager returns a media object
      setNewAd({ ...newAd, mediaId: value.id });
      setSelectedMedia(value);
      setShowMediaPicker(false);
    } else if (sourceType === "URL" && typeof value === "string") {
      // URL source - could be used for video URL
      // For now, we'll just use it if it's a video URL
      if (mediaType.toLowerCase() === "video") {
        setNewAd({ ...newAd, videoUrl: value });
      }
      setShowMediaPicker(false);
    } else if (sourceType === "Upload" && value) {
      // Upload returns the uploaded file/media object
      if (value.id) {
        setNewAd({ ...newAd, mediaId: value.id });
        setSelectedMedia(value);
        setShowMediaPicker(false);
      }
    }
  };

  // Handle adding a new ad
  const handleAddAd = () => {
    if (!windowDisplayId) {
      alert("Please save the Window Display first before adding ads.");
      return;
    }
    if (!newAd.mediaId && !newAd.videoUrl) {
      alert("Please select an image or enter a video URL.");
      return;
    }
    const adData: WindowDisplayAdCreate = {
      windowDisplayId: windowDisplayId,
      mediaId: newAd.mediaId,
      videoUrl: newAd.videoUrl,
    };
    createAd.mutate(adData, {
      ...adSaveHandlers,
      onSuccess: () => {
        setNewAd({});
        setSelectedMedia(null);
        adSaveHandlers.onSuccess();
      },
    });
  };

  // Handle updating an existing ad
  const handleUpdateAd = () => {
    if (!editingAd || !("id" in editingAd)) return;

    updateAd.mutate({
      id: editingAd.id,
      windowDisplayId: editingAd.windowDisplayId,
      mediaId: newAd.mediaId,
      videoUrl: newAd.videoUrl,
    }, {
      ...adSaveHandlers,
      onSuccess: () => {
        setNewAd({});
        setSelectedMedia(null);
        setEditingAd(null);
        adSaveHandlers.onSuccess();
      },
    });
  };

  // Handle saving ad (either create or update)
  const handleSaveAd = () => {
    if (editingAd && "id" in editingAd) {
      handleUpdateAd();
    } else {
      handleAddAd();
    }
  };

  // Handle editing an ad
  const handleEditAd = (ad: any) => {
    setEditingAd({
      id: ad.id,
      windowDisplayId: ad.windowDisplayId,
      mediaId: ad.mediaId,
      videoUrl: ad.videoUrl,
    });
    setNewAd({ mediaId: ad.mediaId, videoUrl: ad.videoUrl });
    if (ad.media) {
      setSelectedMedia(ad.media);
    } else if (ad.mediaId && agencyMedia.data) {
      const media = agencyMedia.data.find((m: any) => m.id === ad.mediaId);
      if (media) setSelectedMedia(media);
    }
    setShowMediaPicker(false);
  };

  // Handle deleting an ad
  const handleDeleteAd = (adId: string) => {
    if (window.confirm("Are you sure you want to delete this ad?")) {
      removeAd.mutate(adId, adSaveHandlers);
    }
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingAd(null);
    setNewAd({});
    setSelectedMedia(null);
  };

  // Handle clearing selected media
  const handleClearMedia = () => {
    setNewAd({ ...newAd, mediaId: undefined });
    setSelectedMedia(null);
  };

  return (
    <>
      <Modal open={openLayouts} setOpen={setOpenLayouts} width={"max-w-6xl"}>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Layout Styles</h3>
          <div className="mb-6 grid gap-8 grid-cols-1 lg:grid-cols-3">

            {layoutStyleOptions.map((option) => (
              option.example && (
                <div key={option.id}>
                  <h4 className="text-md font-medium text-gray-800 mb-2">{option.name}</h4>
                  <img src={option.example} alt={option.name} className="border border-gray-300 rounded" />
                </div>
              )

            ))}
          </div>
        </div>
      </Modal>
      <div className="divide-y space-y-6">
        <div className="-mx-6 px-6">
          <h3 className="text-xl font-medium text-gray-900">
            Window Display Settings
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Customise your Window Displays here.
          </p>
        </div>

        {/* Form Section */}
        <div className="space-y-4">

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8 pt-4">

            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-8 items-center">
              <div>
                <label
                  htmlFor="backgroundColour"
                  className="block text-sm font-medium text-gray-700"
                >
                  Background Colour
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span
                    className="w-2/12 md:w-1/12 inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
                    style={{ backgroundColor: backgroundColour }}
                  ></span>
                  <input
                    {...register("backgroundColour")}
                    type="text"
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-etpink-500 focus:border-etpink-500 sm:text-sm border-gray-300"
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="backgroundColour"
                  className="block text-sm font-medium text-gray-700"
                >
                  Text Colour
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span
                    className="w-2/12 md:w-1/12 inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
                    style={{ backgroundColor: textColour }}
                  ></span>
                  <input
                    {...register("textColour")}
                    type="text"
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-etpink-500 focus:border-etpink-500 sm:text-sm border-gray-300"
                    placeholder="#000000"
                  />
                </div>
              </div>

              <SwitchInput label={"Key Features"} register={register} registerName={"featureInstead"} setValue={setValue} watch={watch} />
              <SwitchInput label={"Hide Icons"} register={register} registerName={"hideIcons"} setValue={setValue} watch={watch} />


              <SelectInput label="Logo Style" registerName="logoStyle" options={logoStyleOptions} register={register} setValue={setValue} watch={watch} />
              <TextInput label="Logo Width" register={register} registerName="logoWidth" type="number" />
              <div className="relative">
                <SelectInput label="Layout Style" registerName="layoutStyle" options={layoutStyleOptions} register={register} setValue={setValue} watch={watch} />
                <p className="absolute right-0 top-0 text-xs text-etpink-600 cursor-pointer" onClick={() => setOpenLayouts(true)}>View Styles</p>
              </div>
              <TextInput label="Scan for Details" register={register} registerName="qrCodeText" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <TextInput label="Time Per Screen (seconds)" register={register} registerName="timePerScreen" type="number" />
              <TextInput label="Total Properties" register={register} registerName="totalProperties" type="number" />
              <TextInput label="Properties Between Ads" register={register} registerName="propertiesBetweenAds" type="number" />
            </div>

            <div className="pt-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Window Display Ads</h4>

              {/* Existing Ads List */}
              {windowDisplayId && ads.data?.data && ads.data.data.length > 0 && (
                <div className="mb-6 space-y-4">
                  {ads.data.data.map((ad) => (
                    <div key={ad.id} className="border border-gray-200 rounded-lg p-4 flex items-center gap-4">
                      {ad.media && (
                        <div className="w-32 h-32 flex-shrink-0">
                          <img
                            src={ad.media.type.includes("video") ? undefined : `${CDN_URL}${ad.media.path}`}
                            alt={ad.media.altText || "Ad image"}
                            className="w-full h-full object-cover rounded"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        {ad.media && (
                          <p className="text-sm font-medium text-gray-900">{ad.media.name}</p>
                        )}
                        {ad.videoUrl && (
                          <p className="text-sm text-gray-600">Video URL: {ad.videoUrl}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditAd(ad)}
                          className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteAd(ad.id)}
                          className="px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Ad Form */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="text-md font-medium text-gray-900 mb-4">
                  {editingAd ? "Edit Ad" : "Add New Ad"}
                </h5>
                <div className="space-y-4">
                  <div className="flex gap-4">

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Image
                      </label>
                      <div className="flex items-center gap-4">
                        {selectedMedia && (
                          <div className="w-36 h-36 flex-shrink-0">
                            {selectedMedia.type.includes("video") ? (
                              <video className="w-full h-full object-cover rounded" controls>
                                <source src={`${CDN_URL}${selectedMedia.path}`} type={selectedMedia.type} />
                              </video>
                            ) : (
                              <img
                                src={`${CDN_URL}${selectedMedia.path}`}
                                alt={selectedMedia.altText || "Selected image"}
                                className="w-full h-full object-cover rounded"
                              />
                            )}
                          </div>
                        )}
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={() => setShowMediaPicker(true)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            {newAd.mediaId ? "Change Image" : "Select Image"}
                          </button>
                          {(selectedMedia || newAd.mediaId) && (
                            <button
                              type="button"
                              onClick={handleClearMedia}
                              className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Video URL (optional)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newAd.videoUrl || ""}
                          onChange={(e) => setNewAd({ ...newAd, videoUrl: e.target.value })}
                          className="flex-1 block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-etpink-500 focus:border-etpink-500 sm:text-sm"
                          placeholder="https://..."
                        />

                        {identity.role === "Admin" && (
                          <button
                            type="button"
                            onClick={() => setShowVideoPicker(true)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Browse Videos
                          </button>
                        )}

                      </div>
                    </div>

                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleSaveAd}
                      disabled={createAd.isLoading || updateAd.isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-etpink-600 rounded-md hover:bg-etpink-700 disabled:bg-gray-400"
                    >
                      {editingAd ? "Update Ad" : "Add Ad"}
                    </button>
                    {editingAd && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Media Picker Modal */}
            <InsertMediaModal
              allowURL={false}
              allowImage={true}
              allowVideo={false}
              title="Select Image"
              confirmButtonText="Select"
              open={showMediaPicker}
              setOpen={setShowMediaPicker}
              onInsert={handleMediaInsert}
              isAdmin={false}
            />

            {/* BunnyCDN Video Picker Modal */}
            <BunnyVideoSelectModal
              open={showVideoPicker}
              setOpen={setShowVideoPicker}
              videos={videos.data?.data}
              isLoading={videos.isLoading}
              onSelect={(videoUrl) => setNewAd({ ...newAd, videoUrl })}
            />

            <div className="flex justify-end w-full">
              <ButtonInput
                label="Save Window Display"
                isSubmit
                onClick={undefined}
                classes={undefined}
                disabled={create.isLoading || update.isLoading}
              />
            </div>

          </form>
        </div>

        {/* URL Information Collapsible Section */}
        <div className="border-t pt-6">
          <button
            type="button"
            onClick={() => setShowUrls(!showUrls)}
            className="flex items-center justify-between w-full text-left hover:bg-gray-100 py-4 rounded-md"
          >
            <h3 className="text-lg font-medium text-gray-900">
              Window Display URLs <span className="text-xs text-gray-500">(Click to expand)</span>
            </h3>
            {showUrls ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {showUrls && (
            <div className="mt-4 space-y-6 text-sm text-gray-700">
              {/* Display your latest properties automatically */}
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-2">
                  Display your latest properties automatically.
                </h4>
                <p className="mb-4 text-gray-600">
                  To get started, add the relevant URL from the list below in your browser and hide the url bar in the settings.
                </p>

                <h5 className="text-sm font-semibold text-gray-800 mb-2">
                  The urls are as follows:
                </h5>
                <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
                  <li><a href={`${agency?.domain}/window-displays-lite/all/all`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/all/all</a> - all available properties</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/all-sold/all`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/all-sold/all</a> - all properties including sold and let</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sales/all`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sales/all</a> - all available sales properties</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sales-sold/all`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sales-sold/all</a> - all sales properties including sold</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/lettings/all`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/lettings/all</a> - all available rental properties</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/lettings-let/all`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/lettings-let/all</a> - all rental properties including let</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sold/all`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sold/all</a> - sold properties only</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/let/all`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/let/all</a> - let properties only</li>
                </ul>

                <h5 className="text-sm font-semibold text-gray-800 mb-2">
                  To separate them for each branch, you would replace the last 'all' with the branch name as follows:
                </h5>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><a href={`${agency?.domain}/window-displays-lite/all/[branch]`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/all/[branch]</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/all-sold/[branch]`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/all-sold/[branch]</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sales/[branch]`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sales/[branch]</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sales-sold/[branch]`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sales-sold/[branch]</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sold/[branch]`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sold/[branch]</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/lettings/[branch]`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/lettings/[branch]</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/lettings-let/[branch]`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/lettings-let/[branch]</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/let/[branch]`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/let/[branch]</a></li>
                </ul>
              </div>

              {/* Select which properties you wish to display */}
              <div className="border-t pt-6">
                <h4 className="text-base font-semibold text-gray-900 mb-2">
                  Select which properties you wish to display.
                </h4>
                <p className="mb-4 text-gray-600">
                  To get started, click on the properties you wish to add and toggle on the "add to window display option" at the top of the page. You can see a list of all the properties displayed here.
                </p>

                <h5 className="text-sm font-semibold text-gray-800 mb-2">
                  The urls are as follows:
                </h5>
                <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
                  <li><a href={`${agency?.domain}/window-displays-lite/all/all/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/all/all/dashboard</a> - all available properties</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/all-sold/all/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/all-sold/all/dashboard</a> - all properties including sold and let</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sales/all/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sales/all/dashboard</a> - all available sales properties</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sales-sold/all/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sales-sold/all/dashboard</a> - all sales properties including sold</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/lettings/all/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/lettings/all/dashboard</a> - all available rental properties</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/lettings-let/all/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/lettings-let/all/dashboard</a> - all rental properties including let</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sold/all/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sold/all/dashboard</a> - sold properties only</li>
                  <li><a href={`${agency?.domain}/window-displays-lite/let/all/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/let/all/dashboard</a> - let properties only</li>
                </ul>

                <h5 className="text-sm font-semibold text-gray-800 mb-2">
                  To separate them for each branch, you would replace the last 'all' with the branch name as follows:
                </h5>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><a href={`${agency?.domain}/window-displays-lite/all/[branch]/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/all/[branch]/dashboard</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/all-sold/[branch]/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/all-sold/[branch]/dashboard</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sales/[branch]/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sales/[branch]/dashboard</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sales-sold/[branch]/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sales-sold/[branch]/dashboard</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/sold/[branch]/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/sold/[branch]/dashboard</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/lettings/[branch]/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/lettings/[branch]/dashboard</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/lettings-let/[branch]/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/lettings-let/[branch]/dashboard</a></li>
                  <li><a href={`${agency?.domain}/window-displays-lite/let/[branch]/dashboard`} target="_blank" rel="noopener noreferrer" className="text-etpink-600 hover:text-etpink-700 underline">{agency?.domain}/window-displays-lite/let/[branch]/dashboard</a></li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}